import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield, Users, FileText, MapPin, Brain, AlertTriangle, CheckCircle,
  Clock, Target, DollarSign, TrendingUp, Activity, Eye, Search,
  ChevronRight, ArrowLeft, RefreshCw, Filter, BarChart3, Network,
  Zap, AlertOctagon, Map, UserCheck, Briefcase, Lock, Building2,
  LogOut, User, Badge, Hash, Mail, Calendar, Building, Trash2, HelpCircle,
  Bot, Send, Edit
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Agency API instance
const agencyApi = axios.create({
  baseURL: `${BACKEND_URL}/api`,
});

agencyApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('agency_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ==================== AGENCY TUTORIAL COMPONENT ====================

const AgencyTutorialModal = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  
  const steps = [
    {
      title: "Welcome, Agent! 🛡️",
      icon: Shield,
      content: "Welcome to Wysspr's Law Enforcement Command Center. This secure portal gives you access to advanced tools for tracking and capturing fugitives.",
      highlight: "Agency-only access granted"
    },
    {
      title: "Command Center",
      icon: BarChart3,
      content: "Your dashboard shows real-time statistics, tip queues, and fugitive tracking status. Monitor all activity from one place.",
      highlight: "Central intelligence hub"
    },
    {
      title: "Tip Management",
      icon: FileText,
      content: "Review incoming tips, verify information, and update tip statuses. Prioritize based on urgency and credibility.",
      highlight: "Process tips efficiently"
    },
    {
      title: "Database Sync",
      icon: RefreshCw,
      content: "Sync fugitive data from FBI, US Marshals, DEA, ATF, and ICE. Keep your database current with the latest intel.",
      highlight: "Multi-agency integration"
    },
    {
      title: "Predictive Analysis",
      icon: Brain,
      content: "Access AI-powered analytics, heat maps, and network graphs to identify patterns and predict fugitive movements.",
      highlight: "AI-enhanced intelligence"
    },
    {
      title: "User Management",
      icon: Users,
      content: "Super Admins can approve new agencies, manage users, and oversee platform activity.",
      highlight: "Full administrative control"
    }
  ];
  
  if (!isOpen) return null;
  
  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      localStorage.setItem('tutorial_agency_completed', 'true');
      onClose();
    }
  };
  
  const handlePrev = () => {
    if (currentStep > 0) setCurrentStep(prev => prev - 1);
  };
  
  const handleSkip = () => {
    localStorage.setItem('tutorial_agency_completed', 'true');
    onClose();
  };
  
  const step = steps[currentStep];
  const Icon = step.icon;
  
  return (
    <AnimatePresence>
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-slate-900 border border-amber-500/30 rounded-lg max-w-lg w-full p-6"
        >
          <div className="flex gap-1 mb-6">
            {steps.map((_, i) => (
              <div
                key={i}
                className={`h-1 flex-1 rounded-full transition-all ${
                  i <= currentStep ? 'bg-amber-500' : 'bg-slate-700'
                }`}
              />
            ))}
          </div>
          
          <div className="flex justify-center mb-4">
            <div className="p-4 bg-amber-500/20 rounded-full">
              <Icon className="w-12 h-12 text-amber-400" />
            </div>
          </div>
          
          <h2 className="font-bold text-2xl text-white text-center mb-3">{step.title}</h2>
          <p className="text-slate-300 text-center mb-4">{step.content}</p>
          <div className="bg-amber-500/10 border border-amber-500/30 rounded p-3 mb-6">
            <p className="text-amber-400 text-sm text-center font-semibold">💡 {step.highlight}</p>
          </div>
          
          <p className="text-slate-500 text-sm text-center mb-4">Step {currentStep + 1} of {steps.length}</p>
          
          <div className="flex gap-3">
            <button onClick={handleSkip} className="flex-1 py-2 text-slate-400 hover:text-white transition-colors text-sm">
              Skip Tutorial
            </button>
            <button onClick={handlePrev} disabled={currentStep === 0} className="px-4 py-2 bg-slate-800 text-white rounded disabled:opacity-50">
              Back
            </button>
            <button onClick={handleNext} className="flex-1 py-2 bg-amber-500 text-slate-900 font-semibold rounded hover:bg-amber-400">
              {currentStep === steps.length - 1 ? "Get Started!" : "Next"}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const AgencyTutorialButton = ({ onClick }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-2 text-slate-400 hover:text-amber-400 transition-colors text-sm"
    title="View Tutorial"
  >
    <HelpCircle className="w-4 h-4" />
    <span className="hidden md:inline">Tutorial</span>
  </button>
);

// Agency Auth Context
export const AgencyAuthContext = React.createContext(null);

export const useAgencyAuth = () => {
  const context = React.useContext(AgencyAuthContext);
  if (!context) {
    throw new Error('useAgencyAuth must be used within AgencyAuthProvider');
  }
  return context;
};

export const AgencyAuthProvider = ({ children }) => {
  const [agencyUser, setAgencyUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('agency_token');
    const userData = localStorage.getItem('agency_user');
    if (token && userData) {
      setAgencyUser(JSON.parse(userData));
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/agency/login`, { email, password });
      localStorage.setItem('agency_token', res.data.token);
      localStorage.setItem('agency_user', JSON.stringify(res.data.user));
      setAgencyUser(res.data.user);
      return res.data;
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('agency_token');
    localStorage.removeItem('agency_user');
    setAgencyUser(null);
    window.location.href = '/agency/login';
  };

  const register = async (data) => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/agency/user/register`, data);
      localStorage.setItem('agency_token', res.data.token);
      localStorage.setItem('agency_user', JSON.stringify(res.data.user));
      setAgencyUser(res.data.user);
      return res.data;
    } catch (error) {
      throw error;
    }
  };

  return (
    <AgencyAuthContext.Provider value={{ agencyUser, login, logout, register, loading }}>
      {children}
    </AgencyAuthContext.Provider>
  );
};

// Agency Login Page
export const AgencyLoginPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [badgeNumber, setBadgeNumber] = useState('');
  const [agencyId, setAgencyId] = useState('');
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(false);
  const { login, register } = useAgencyAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Fetch available agencies
    axios.get(`${BACKEND_URL}/api/agencies/list`).then(res => {
      setAgencies(res.data || []);
    }).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
        toast.success('Welcome back, Agent');
        navigate('/agency/dashboard');
      } else {
        await register({ email, password, name, badge_number: badgeNumber, agency_id: agencyId });
        toast.success('Registration successful');
        navigate('/agency/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4 pt-20">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Shield className="w-12 h-12 text-cyan-400" />
            <div>
              <h1 className="font-heading font-bold text-3xl text-white uppercase tracking-wider">WYSSPR</h1>
              <p className="text-cyan-400 text-sm tracking-wider">LAW ENFORCEMENT PORTAL</p>
            </div>
          </div>
          <p className="text-slate-400">Secure access for authorized personnel only</p>
        </div>

        <div className="card-tactical p-8">
          <div className="flex mb-6">
            <button
              onClick={() => setIsLogin(true)}
              className={`flex-1 py-2 text-sm font-semibold ${isLogin ? 'text-cyan-400 border-b-2 border-cyan-400' : 'text-slate-400'}`}
            >
              Sign In
            </button>
            <button
              onClick={() => setIsLogin(false)}
              className={`flex-1 py-2 text-sm font-semibold ${!isLogin ? 'text-cyan-400 border-b-2 border-cyan-400' : 'text-slate-400'}`}
            >
              Register
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <>
                <div>
                  <label className="block text-slate-400 text-xs uppercase tracking-wider mb-1">Full Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full input-tactical px-3 py-2"
                    placeholder="Agent Name"
                    required={!isLogin}
                  />
                </div>
                <div>
                  <label className="block text-slate-400 text-xs uppercase tracking-wider mb-1">Badge Number</label>
                  <input
                    type="text"
                    value={badgeNumber}
                    onChange={(e) => setBadgeNumber(e.target.value)}
                    className="w-full input-tactical px-3 py-2"
                    placeholder="Badge #"
                  />
                </div>
                <div>
                  <label className="block text-slate-400 text-xs uppercase tracking-wider mb-1">Agency</label>
                  <input
                    type="text"
                    value={agencyId}
                    onChange={(e) => setAgencyId(e.target.value)}
                    className="w-full input-tactical px-3 py-2"
                    placeholder="Agency ID (contact admin)"
                    required={!isLogin}
                  />
                </div>
              </>
            )}
            <div>
              <label className="block text-slate-400 text-xs uppercase tracking-wider mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full input-tactical px-3 py-2"
                placeholder="agent@agency.gov"
                required
              />
            </div>
            <div>
              <label className="block text-slate-400 text-xs uppercase tracking-wider mb-1">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full input-tactical px-3 py-2"
                placeholder="••••••••"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary py-3 flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Lock className="w-4 h-4" />
                  {isLogin ? 'Secure Login' : 'Register'}
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <a href="/" className="text-slate-500 hover:text-cyan-400 text-sm">
              ← Return to Public Site
            </a>
          </div>
        </div>

        <p className="text-center text-slate-600 text-xs mt-6">
          This portal is restricted to authorized law enforcement personnel.
          Unauthorized access is prohibited and may be subject to prosecution.
        </p>
      </motion.div>
    </div>
  );
};

// Super Admin Panel Component
const SuperAdminPanel = ({ fetchData }) => {
  const [activeTab, setActiveTab] = useState('agencies');
  const [pendingAgencies, setPendingAgencies] = useState([]);
  const [verifiedAgencies, setVerifiedAgencies] = useState([]);
  const [rejectedAgencies, setRejectedAgencies] = useState([]);
  const [agencyUsers, setAgencyUsers] = useState([]);
  const [allAgencies, setAllAgencies] = useState([]);
  const [syncStatus, setSyncStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [editingAgency, setEditingAgency] = useState(null);
  const [editForm, setEditForm] = useState({});

  const fetchAgencies = useCallback(async () => {
    try {
      const res = await agencyApi.get('/agency/admin/pending-agencies');
      setPendingAgencies(res.data.pending || []);
      setVerifiedAgencies(res.data.verified || []);
      setRejectedAgencies(res.data.rejected || []);
    } catch (error) {
      toast.error('Failed to load agencies');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAllAgencies = useCallback(async () => {
    try {
      const res = await agencyApi.get('/agency/admin/all-agencies');
      setAllAgencies(res.data || []);
    } catch (error) {
      toast.error('Failed to load all agencies');
    }
  }, []);

  const fetchAgencyUsers = useCallback(async () => {
    try {
      const res = await agencyApi.get('/agency/admin/all-users');
      setAgencyUsers(res.data || []);
    } catch (error) {
      toast.error('Failed to load agency users');
    }
  }, []);

  useEffect(() => {
    fetchAgencies();
  }, [fetchAgencies]);

  useEffect(() => {
    if (activeTab === 'users') {
      fetchAgencyUsers();
    }
  }, [activeTab, fetchAgencyUsers]);

  useEffect(() => {
    if (activeTab === 'edit') {
      fetchAllAgencies();
    }
  }, [activeTab, fetchAllAgencies]);

  const handleVerifyAgency = async (agencyId, action) => {
    setActionLoading(agencyId);
    try {
      const notes = action === 'REJECT' ? prompt('Enter rejection reason (optional):') : null;
      await agencyApi.put(`/agency/admin/verify/${agencyId}?action=${action}${notes ? `&notes=${encodeURIComponent(notes)}` : ''}`);
      toast.success(`Agency ${action.toLowerCase()}d successfully`);
      fetchAgencies();
    } catch (error) {
      toast.error(`Failed to ${action.toLowerCase()} agency`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleEditAgency = (agency) => {
    setEditingAgency(agency);
    setEditForm({
      name: agency.name,
      agency_type: agency.agency_type,
      jurisdiction: agency.jurisdiction,
      contact_email: agency.contact_email,
      contact_phone: agency.contact_phone || '',
      address: agency.address || '',
      subscription_tier: agency.subscription_tier || 'basic'
    });
  };

  const handleUpdateAgency = async () => {
    if (!editingAgency) return;
    
    setActionLoading(editingAgency.agency_id);
    try {
      await agencyApi.put(`/agency/admin/agency/${editingAgency.agency_id}`, editForm);
      toast.success('Agency updated successfully');
      setEditingAgency(null);
      fetchAllAgencies();
      fetchAgencies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update agency');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteAgency = async (agencyId, agencyName) => {
    if (!window.confirm(`Are you sure you want to delete "${agencyName}" and ALL its users? This action cannot be undone.`)) {
      return;
    }
    
    setActionLoading(agencyId);
    try {
      await agencyApi.delete(`/agency/admin/agency/${agencyId}`);
      toast.success(`Agency "${agencyName}" deleted successfully`);
      fetchAllAgencies();
      fetchAgencies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete agency');
    } finally {
      setActionLoading(null);
    }
  };

  const syncAgencyDatabase = async (agencyName) => {
    setSyncStatus(prev => ({ ...prev, [agencyName]: 'syncing' }));
    try {
      const res = await agencyApi.post(`/agency/sync/live/${agencyName}`);
      setSyncStatus(prev => ({ ...prev, [agencyName]: 'success' }));
      toast.success(`Synced ${res.data.synced_count} fugitives from ${agencyName}`);
      setTimeout(() => setSyncStatus(prev => ({ ...prev, [agencyName]: null })), 3000);
    } catch (error) {
      setSyncStatus(prev => ({ ...prev, [agencyName]: 'error' }));
      toast.error(`Failed to sync ${agencyName}`);
    }
  };

  const syncAllDatabases = async () => {
    setSyncing(true);
    try {
      const res = await agencyApi.post('/agency/sync/all-live');
      toast.success(`Synced ${res.data.total} fugitives from all agencies`);
      fetchData();
    } catch (error) {
      toast.error('Failed to sync all databases');
    } finally {
      setSyncing(false);
    }
  };

  const cleanupStaticData = async () => {
    if (!window.confirm('This will remove all static/placeholder fugitive data (non-FBI entries). Continue?')) {
      return;
    }
    setSyncing(true);
    try {
      const res = await agencyApi.delete('/agency/cleanup-static-data');
      toast.success(`Cleaned up ${res.data.deleted_count} static entries. ${res.data.remaining_fbi} FBI records remain.`);
      fetchData();
    } catch (error) {
      toast.error('Failed to cleanup static data');
    } finally {
      setSyncing(false);
    }
  };

  const deleteAgencyUser = async (userId, userEmail) => {
    if (!window.confirm(`Are you sure you want to delete agency user "${userEmail}"? This action cannot be undone.`)) {
      return;
    }
    setActionLoading(userId);
    try {
      await agencyApi.delete(`/agency/admin/users/${userId}`);
      toast.success(`User ${userEmail} deleted successfully`);
      fetchAgencyUsers();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || "Cannot delete this user");
      } else {
        toast.error("Failed to delete user");
      }
    } finally {
      setActionLoading(null);
    }
  };

  const agencies = [
    { id: 'FBI', name: 'FBI', color: 'blue', icon: '🔵' },
    { id: 'US_MARSHALS', name: 'US Marshals', color: 'purple', icon: '⚔️' },
    { id: 'DEA', name: 'DEA', color: 'green', icon: '💊' },
    { id: 'ATF', name: 'ATF', color: 'orange', icon: '🔫' },
    { id: 'ICE', name: 'ICE', color: 'cyan', icon: '🛂' }
  ];

  return (
    <div className="mt-8 space-y-6">
      {/* Panel Header */}
      <div className="card-tactical p-6 border-purple-500/30">
        <h3 className="font-heading font-bold text-lg text-white uppercase flex items-center gap-2 mb-4">
          <Zap className="w-5 h-5 text-purple-400" />
          Super Admin Controls
        </h3>
        
        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={() => setActiveTab('agencies')}
            className={`px-4 py-2 rounded-sm text-sm font-semibold transition-colors ${
              activeTab === 'agencies' 
                ? 'bg-purple-500 text-white' 
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            <Building2 className="w-4 h-4 inline mr-2" />
            Agency Verification ({pendingAgencies.length})
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`px-4 py-2 rounded-sm text-sm font-semibold transition-colors ${
              activeTab === 'users' 
                ? 'bg-red-500 text-white' 
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            <Users className="w-4 h-4 inline mr-2" />
            Manage Users ({agencyUsers.length})
          </button>
          <button
            onClick={() => setActiveTab('databases')}
            className={`px-4 py-2 rounded-sm text-sm font-semibold transition-colors ${
              activeTab === 'databases' 
                ? 'bg-cyan-500 text-white' 
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            <RefreshCw className="w-4 h-4 inline mr-2" />
            Database Sync
          </button>
          <button
            onClick={() => setActiveTab('demo')}
            className={`px-4 py-2 rounded-sm text-sm font-semibold transition-colors ${
              activeTab === 'demo' 
                ? 'bg-amber-500 text-white' 
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            <Zap className="w-4 h-4 inline mr-2" />
            Demo Mode
          </button>
          <button
            onClick={() => setActiveTab('edit')}
            className={`px-4 py-2 rounded-sm text-sm font-semibold transition-colors ${
              activeTab === 'edit' 
                ? 'bg-green-500 text-white' 
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            <Edit className="w-4 h-4 inline mr-2" />
            Edit Agencies
          </button>
        </div>

        {/* Agency Verification Tab */}
        {activeTab === 'agencies' && (
          <div className="space-y-6">
            {/* Pending Agencies */}
            <div>
              <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4 text-amber-400" />
                Pending Verification ({pendingAgencies.length})
              </h4>
              {loading ? (
                <div className="text-center py-4">
                  <RefreshCw className="w-6 h-6 text-cyan-400 animate-spin mx-auto" />
                </div>
              ) : pendingAgencies.length === 0 ? (
                <p className="text-slate-500 text-sm py-4">No pending agency registrations</p>
              ) : (
                <div className="space-y-3">
                  {pendingAgencies.map((agency) => (
                    <div key={agency.agency_id} className="bg-slate-800/50 p-4 rounded-sm border border-amber-500/30">
                      <div className="flex items-start justify-between">
                        <div>
                          <h5 className="text-white font-semibold">{agency.name}</h5>
                          <p className="text-slate-400 text-sm">{agency.agency_type} • {agency.jurisdiction}</p>
                          <p className="text-slate-500 text-xs mt-1">{agency.contact_email}</p>
                          <p className="text-slate-600 text-xs">Registered: {new Date(agency.created_at).toLocaleDateString()}</p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleVerifyAgency(agency.agency_id, 'APPROVE')}
                            disabled={actionLoading === agency.agency_id}
                            className="px-3 py-1.5 bg-green-500/20 text-green-400 rounded-sm text-sm hover:bg-green-500/30 flex items-center gap-1"
                          >
                            {actionLoading === agency.agency_id ? (
                              <RefreshCw className="w-3 h-3 animate-spin" />
                            ) : (
                              <CheckCircle className="w-3 h-3" />
                            )}
                            Approve
                          </button>
                          <button
                            onClick={() => handleVerifyAgency(agency.agency_id, 'REJECT')}
                            disabled={actionLoading === agency.agency_id}
                            className="px-3 py-1.5 bg-red-500/20 text-red-400 rounded-sm text-sm hover:bg-red-500/30 flex items-center gap-1"
                          >
                            <AlertTriangle className="w-3 h-3" />
                            Reject
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Verified Agencies */}
            <div>
              <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                Verified Agencies ({verifiedAgencies.length})
              </h4>
              {verifiedAgencies.length === 0 ? (
                <p className="text-slate-500 text-sm py-2">No verified agencies</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {verifiedAgencies.slice(0, 6).map((agency) => (
                    <div key={agency.agency_id} className="bg-slate-800/30 p-3 rounded-sm border border-green-500/20">
                      <p className="text-white text-sm font-semibold">{agency.name}</p>
                      <p className="text-slate-500 text-xs">{agency.agency_type} • {agency.jurisdiction}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Database Sync Tab */}
        {activeTab === 'databases' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <p className="text-slate-400 text-sm">Sync fugitive data from federal law enforcement databases</p>
              <div className="flex gap-2">
                <button
                  onClick={cleanupStaticData}
                  disabled={syncing}
                  className="btn-secondary px-4 py-2 text-sm flex items-center gap-2 border-red-500/30 hover:border-red-500/50"
                >
                  <Trash2 className="w-4 h-4 text-red-400" />
                  Clean Static Data
                </button>
                <button
                  onClick={syncAllDatabases}
                  disabled={syncing}
                  className="btn-primary px-4 py-2 text-sm flex items-center gap-2"
                >
                  {syncing ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  Sync All Databases
                </button>
              </div>
            </div>
            
            {/* Warning Banner */}
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-sm p-3 mb-4">
              <p className="text-amber-400 text-sm flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                <span>
                  <strong>Note:</strong> Non-FBI syncs may use placeholder data if web scraping fails. 
                  Use "Clean Static Data" to remove any test/placeholder entries.
                </span>
              </p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {agencies.map((agency) => (
                <div key={agency.id} className="bg-slate-800/50 p-4 rounded-sm border border-slate-700 hover:border-cyan-500/50 transition-colors">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xl">{agency.icon}</span>
                    <span className="text-white font-semibold text-sm">{agency.name}</span>
                  </div>
                  <button
                    onClick={() => syncAgencyDatabase(agency.id)}
                    disabled={syncStatus[agency.id] === 'syncing'}
                    className={`w-full px-3 py-2 rounded-sm text-xs font-semibold flex items-center justify-center gap-1 transition-colors ${
                      syncStatus[agency.id] === 'syncing' 
                        ? 'bg-cyan-500/20 text-cyan-400' 
                        : syncStatus[agency.id] === 'success'
                        ? 'bg-green-500/20 text-green-400'
                        : syncStatus[agency.id] === 'error'
                        ? 'bg-red-500/20 text-red-400'
                        : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                    }`}
                  >
                    {syncStatus[agency.id] === 'syncing' ? (
                      <>
                        <RefreshCw className="w-3 h-3 animate-spin" />
                        Syncing...
                      </>
                    ) : syncStatus[agency.id] === 'success' ? (
                      <>
                        <CheckCircle className="w-3 h-3" />
                        Synced!
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-3 h-3" />
                        Sync {agency.name}
                      </>
                    )}
                  </button>
                </div>
              ))}
            </div>
            
            <p className="text-slate-600 text-xs mt-4">
              * FBI data comes directly from the FBI API. Other agencies use web scraping which may occasionally fail.
            </p>
          </div>
        )}

        {/* Users Management Tab */}
        {activeTab === 'users' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <p className="text-slate-400 text-sm">Manage all registered agency users across all agencies</p>
              <button
                onClick={fetchAgencyUsers}
                className="btn-secondary px-4 py-2 text-sm flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>

            {agencyUsers.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No agency users found</p>
            ) : (
              <div className="overflow-x-auto max-h-96 overflow-y-auto">
                <table className="w-full">
                  <thead className="bg-slate-800/50 sticky top-0">
                    <tr>
                      <th className="text-left p-3 text-slate-400 text-sm font-medium">Name</th>
                      <th className="text-left p-3 text-slate-400 text-sm font-medium">Email</th>
                      <th className="text-left p-3 text-slate-400 text-sm font-medium">Agency</th>
                      <th className="text-left p-3 text-slate-400 text-sm font-medium">Role</th>
                      <th className="text-left p-3 text-slate-400 text-sm font-medium">Badge #</th>
                      <th className="text-left p-3 text-slate-400 text-sm font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {agencyUsers.map((u) => (
                      <tr key={u.user_id} className="hover:bg-slate-800/30">
                        <td className="p-3 text-white">{u.name || 'N/A'}</td>
                        <td className="p-3 text-slate-400">{u.email}</td>
                        <td className="p-3 text-cyan-400 text-sm">{u.agency_name || 'Unknown'}</td>
                        <td className="p-3">
                          <span className={`px-2 py-0.5 rounded-sm text-xs ${
                            u.role === 'super_admin' ? 'bg-purple-500/20 text-purple-400' :
                            u.role === 'agency_admin' ? 'bg-amber-500/20 text-amber-400' :
                            'bg-slate-700 text-slate-400'
                          }`}>
                            {u.role?.replace('_', ' ').toUpperCase() || 'Agent'}
                          </span>
                        </td>
                        <td className="p-3 text-slate-500 text-sm font-mono">{u.badge_number || 'N/A'}</td>
                        <td className="p-3">
                          {u.role !== 'super_admin' ? (
                            <button
                              onClick={() => deleteAgencyUser(u.user_id, u.email)}
                              disabled={actionLoading === u.user_id}
                              className="px-3 py-1 bg-red-500/20 text-red-400 rounded-sm text-xs hover:bg-red-500/30 flex items-center gap-1"
                              title="Delete user"
                            >
                              {actionLoading === u.user_id ? (
                                <RefreshCw className="w-3 h-3 animate-spin" />
                              ) : (
                                <AlertTriangle className="w-3 h-3" />
                              )}
                              Delete
                            </button>
                          ) : (
                            <span className="text-slate-600 text-xs">Protected</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Demo Mode Tab */}
        {activeTab === 'demo' && (
          <div className="flex gap-4">
            <button 
              onClick={async () => {
                try {
                  await agencyApi.post('/agency/demo/setup');
                  toast.success('Demo mode activated');
                  fetchData();
                } catch (error) {
                  toast.error('Failed to setup demo');
                }
              }}
              className="btn-primary px-4 py-2 text-sm"
            >
              Setup Demo Data
            </button>
            <button 
              onClick={async () => {
                try {
                  await agencyApi.delete('/agency/demo/cleanup');
                  toast.success('Demo data removed');
                  fetchData();
                } catch (error) {
                  toast.error('Failed to cleanup');
                }
              }}
              className="btn-secondary px-4 py-2 text-sm"
            >
              Cleanup Demo Data
            </button>
          </div>
        )}

        {/* Edit Agencies Tab */}
        {activeTab === 'edit' && (
          <div className="space-y-4">
            <p className="text-slate-400 text-sm mb-4">Manage and edit all registered agencies</p>
            
            {/* Edit Modal */}
            {editingAgency && (
              <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                <div className="bg-slate-900 border border-slate-700 rounded-lg max-w-lg w-full p-6">
                  <h3 className="font-heading font-bold text-lg text-white mb-4">
                    Edit Agency: {editingAgency.name}
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-slate-400 text-sm mb-1">Agency Name</label>
                      <input
                        type="text"
                        value={editForm.name}
                        onChange={(e) => setEditForm(f => ({ ...f, name: e.target.value }))}
                        className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-400 text-sm mb-1">Agency Type</label>
                      <select
                        value={editForm.agency_type}
                        onChange={(e) => setEditForm(f => ({ ...f, agency_type: e.target.value }))}
                        className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white"
                      >
                        <option value="FBI">FBI</option>
                        <option value="US_MARSHALS">US Marshals</option>
                        <option value="DEA">DEA</option>
                        <option value="ATF">ATF</option>
                        <option value="ICE">ICE</option>
                        <option value="STATE_POLICE">State Police</option>
                        <option value="LOCAL_POLICE">Local Police</option>
                        <option value="SECRET_SERVICE">Secret Service</option>
                        <option value="OTHER">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-slate-400 text-sm mb-1">Jurisdiction</label>
                      <input
                        type="text"
                        value={editForm.jurisdiction}
                        onChange={(e) => setEditForm(f => ({ ...f, jurisdiction: e.target.value }))}
                        className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-400 text-sm mb-1">Contact Email</label>
                      <input
                        type="email"
                        value={editForm.contact_email}
                        onChange={(e) => setEditForm(f => ({ ...f, contact_email: e.target.value }))}
                        className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-400 text-sm mb-1">Contact Phone</label>
                      <input
                        type="text"
                        value={editForm.contact_phone}
                        onChange={(e) => setEditForm(f => ({ ...f, contact_phone: e.target.value }))}
                        className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-400 text-sm mb-1">Address</label>
                      <input
                        type="text"
                        value={editForm.address}
                        onChange={(e) => setEditForm(f => ({ ...f, address: e.target.value }))}
                        className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-400 text-sm mb-1">Subscription Tier</label>
                      <select
                        value={editForm.subscription_tier}
                        onChange={(e) => setEditForm(f => ({ ...f, subscription_tier: e.target.value }))}
                        className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white"
                      >
                        <option value="basic">Basic</option>
                        <option value="pro">Pro</option>
                        <option value="enterprise">Enterprise</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={() => setEditingAgency(null)}
                      className="flex-1 px-4 py-2 bg-slate-700 text-white rounded hover:bg-slate-600"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleUpdateAgency}
                      disabled={actionLoading === editingAgency.agency_id}
                      className="flex-1 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-500 disabled:opacity-50"
                    >
                      {actionLoading === editingAgency.agency_id ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Agencies List */}
            {allAgencies.length === 0 ? (
              <p className="text-slate-500 text-sm py-4">No agencies found</p>
            ) : (
              <div className="space-y-3">
                {allAgencies.map((agency) => (
                  <div key={agency.agency_id} className="bg-slate-800/50 p-4 rounded-sm border border-slate-700 hover:border-green-500/30 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h5 className="text-white font-semibold">{agency.name}</h5>
                          {agency.is_verified ? (
                            <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded">Verified</span>
                          ) : (
                            <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded">Pending</span>
                          )}
                          <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded capitalize">
                            {agency.subscription_tier || 'basic'}
                          </span>
                        </div>
                        <p className="text-slate-400 text-sm mt-1">{agency.agency_type} • {agency.jurisdiction}</p>
                        <p className="text-slate-500 text-xs mt-1">{agency.contact_email}</p>
                        {agency.contact_phone && <p className="text-slate-600 text-xs">{agency.contact_phone}</p>}
                        <p className="text-slate-600 text-xs mt-1">Users: {agency.user_count || 0} • Created: {new Date(agency.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditAgency(agency)}
                          className="px-3 py-1.5 bg-green-500/20 text-green-400 rounded-sm text-sm hover:bg-green-500/30 flex items-center gap-1"
                        >
                          <Edit className="w-3 h-3" />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteAgency(agency.agency_id, agency.name)}
                          disabled={actionLoading === agency.agency_id}
                          className="px-3 py-1.5 bg-red-500/20 text-red-400 rounded-sm text-sm hover:bg-red-500/30 flex items-center gap-1"
                        >
                          {actionLoading === agency.agency_id ? (
                            <RefreshCw className="w-3 h-3 animate-spin" />
                          ) : (
                            <Trash2 className="w-3 h-3" />
                          )}
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Agency Dashboard
export const AgencyDashboard = () => {
  const { agencyUser, logout } = useAgencyAuth();
  const [stats, setStats] = useState(null);
  const [recentTips, setRecentTips] = useState([]);
  const [activeCases, setActiveCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTutorial, setShowTutorial] = useState(false);
  const navigate = useNavigate();

  // Check if first time agency user
  useEffect(() => {
    const tutorialCompleted = localStorage.getItem('tutorial_agency_completed');
    if (!tutorialCompleted) {
      setShowTutorial(true);
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, tipsRes, casesRes] = await Promise.all([
        agencyApi.get('/agency/dashboard/stats'),
        agencyApi.get('/agency/tips?limit=5'),
        agencyApi.get('/agency/cases?status=active')
      ]);
      setStats(statsRes.data);
      setRecentTips(tipsRes.data.tips || []);
      setActiveCases(casesRes.data || []);
    } catch (error) {
      if (error.response?.status === 401) {
        logout();
        navigate('/agency/login');
      }
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [logout, navigate]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, [fetchData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Agency Navbar */}
      <nav className="fixed top-0 w-full z-50 bg-slate-900/95 backdrop-blur-xl border-b border-cyan-500/30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-cyan-400" />
            <div>
              <span className="font-heading font-bold text-xl text-white">WYSSPR</span>
              <span className="text-cyan-400 text-xs ml-2">AGENCY PORTAL</span>
            </div>
          </div>
          <div className="flex items-center gap-6">
            <button onClick={() => navigate('/agency/tips')} className="text-slate-300 hover:text-cyan-400 text-sm flex items-center gap-1">
              <FileText className="w-4 h-4" />
              Tips
            </button>
            <button onClick={() => navigate('/agency/cases')} className="text-slate-300 hover:text-cyan-400 text-sm flex items-center gap-1">
              <Briefcase className="w-4 h-4" />
              Cases
            </button>
            <button onClick={() => navigate('/agency/fugitives')} className="text-slate-300 hover:text-cyan-400 text-sm flex items-center gap-1">
              <Target className="w-4 h-4" />
              Fugitives
            </button>
            <button onClick={() => navigate('/agency/analytics')} className="text-slate-300 hover:text-cyan-400 text-sm flex items-center gap-1">
              <Brain className="w-4 h-4" />
              AI Analysis
            </button>
            <div className="flex items-center gap-2 pl-4 border-l border-slate-700">
              <button onClick={() => navigate('/agency/profile')} className="text-right hover:opacity-80 transition-opacity">
                <p className="text-white text-sm font-semibold">{agencyUser?.name}</p>
                <p className="text-slate-500 text-xs">{agencyUser?.agency_name}</p>
              </button>
              <button onClick={logout} className="text-slate-400 hover:text-red-400 text-sm ml-2">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Dashboard Content */}
      <div className="pt-20 px-4 pb-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-cyan-400 font-mono text-sm tracking-[0.2em] mb-2">COMMAND CENTER</p>
              <h1 className="font-heading font-bold text-3xl text-white uppercase">Agency Dashboard</h1>
              <p className="text-slate-400 mt-2">Real-time intelligence and case management</p>
            </div>
            <AgencyTutorialButton onClick={() => setShowTutorial(true)} />
          </div>

          {/* Agency Tutorial Modal */}
          <AgencyTutorialModal isOpen={showTutorial} onClose={() => setShowTutorial(false)} />

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="card-tactical p-4">
              <div className="flex items-center justify-between mb-2">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                <span className="text-xs text-slate-500">24H</span>
              </div>
              <p className="text-3xl font-bold text-red-400">{stats?.tips?.high_priority_24h || 0}</p>
              <p className="text-slate-400 text-sm">High Priority Tips</p>
            </div>
            <div className="card-tactical p-4">
              <div className="flex items-center justify-between mb-2">
                <Clock className="w-5 h-5 text-amber-400" />
              </div>
              <p className="text-3xl font-bold text-amber-400">{stats?.tips?.pending || 0}</p>
              <p className="text-slate-400 text-sm">Pending Review</p>
            </div>
            <div className="card-tactical p-4">
              <div className="flex items-center justify-between mb-2">
                <Briefcase className="w-5 h-5 text-cyan-400" />
              </div>
              <p className="text-3xl font-bold text-cyan-400">{stats?.cases?.active || 0}</p>
              <p className="text-slate-400 text-sm">Active Cases</p>
            </div>
            <div className="card-tactical p-4">
              <div className="flex items-center justify-between mb-2">
                <Target className="w-5 h-5 text-green-400" />
              </div>
              <p className="text-3xl font-bold text-green-400">{stats?.captures || 0}</p>
              <p className="text-slate-400 text-sm">Total Captures</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Recent Tips */}
            <div className="card-tactical p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-bold text-lg text-white uppercase flex items-center gap-2">
                  <FileText className="w-5 h-5 text-cyan-400" />
                  Recent Tips
                </h3>
                <button onClick={() => navigate('/agency/tips')} className="text-cyan-400 text-sm flex items-center gap-1">
                  View All <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              <div className="space-y-3">
                {recentTips.length === 0 ? (
                  <p className="text-slate-500 text-center py-4">No tips yet</p>
                ) : (
                  recentTips.map((tip) => (
                    <div key={tip.tip_id} className="bg-slate-800/50 p-3 rounded-sm border-l-2 border-l-cyan-500">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-white font-semibold text-sm">{tip.fugitive_name}</p>
                          <p className="text-slate-400 text-xs line-clamp-1">{tip.content}</p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded ${
                          tip.priority === 'CRITICAL' ? 'bg-red-500/20 text-red-400' :
                          tip.priority === 'HIGH' ? 'bg-amber-500/20 text-amber-400' :
                          'bg-slate-700 text-slate-400'
                        }`}>
                          {tip.priority}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {tip.location || 'N/A'}</span>
                        <span>{tip.tipster_id}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Active Cases */}
            <div className="card-tactical p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-bold text-lg text-white uppercase flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-cyan-400" />
                  Active Cases
                </h3>
                <button onClick={() => navigate('/agency/cases')} className="text-cyan-400 text-sm flex items-center gap-1">
                  View All <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              <div className="space-y-3">
                {activeCases.length === 0 ? (
                  <p className="text-slate-500 text-center py-4">No active cases</p>
                ) : (
                  activeCases.slice(0, 4).map((caseItem) => (
                    <div key={caseItem.case_id} className="bg-slate-800/50 p-3 rounded-sm flex items-center gap-4">
                      <div className="w-12 h-12 rounded-sm overflow-hidden bg-slate-700">
                        {caseItem.fugitive_image && (
                          <img src={caseItem.fugitive_image} alt="" className="w-full h-full object-cover" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-semibold text-sm">{caseItem.fugitive_name}</p>
                        <p className="text-slate-500 text-xs">Case #{caseItem.case_number}</p>
                      </div>
                      <div className="text-right">
                        <span className={`text-xs px-2 py-1 rounded ${
                          caseItem.status === 'surveillance' ? 'bg-amber-500/20 text-amber-400' :
                          caseItem.status === 'apprehension_planned' ? 'bg-red-500/20 text-red-400' :
                          'bg-cyan-500/20 text-cyan-400'
                        }`}>
                          {caseItem.status.replace('_', ' ')}
                        </span>
                        <p className="text-slate-500 text-xs mt-1">{caseItem.tip_count} tips</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            <button onClick={() => navigate('/agency/tips')} className="card-tactical p-4 hover:border-cyan-500/50 transition-colors group">
              <Search className="w-6 h-6 text-cyan-400 mb-2" />
              <p className="text-white font-semibold">Review Tips</p>
              <p className="text-slate-500 text-sm">Pending: {stats?.tips?.pending || 0}</p>
            </button>
            <button onClick={() => navigate('/agency/ai')} className="card-tactical p-4 hover:border-amber-500/50 transition-colors group bg-gradient-to-br from-amber-500/10 to-transparent border-amber-500/30">
              <Brain className="w-6 h-6 text-amber-400 mb-2" />
              <p className="text-white font-semibold">AI Command</p>
              <p className="text-amber-400 text-xs">ADVANCED</p>
            </button>
            <button onClick={() => navigate('/agency/analytics')} className="card-tactical p-4 hover:border-cyan-500/50 transition-colors group">
              <Target className="w-6 h-6 text-purple-400 mb-2" />
              <p className="text-white font-semibold">Predictive</p>
              <p className="text-slate-500 text-sm">Location Intel</p>
            </button>
            <button onClick={() => navigate('/agency/fugitives')} className="card-tactical p-4 hover:border-cyan-500/50 transition-colors group">
              <Users className="w-6 h-6 text-red-400 mb-2" />
              <p className="text-white font-semibold">Fugitives</p>
              <p className="text-slate-500 text-sm">Database</p>
            </button>
          </div>

          {/* Super Admin Controls */}
          {agencyUser?.role === 'super_admin' && (
            <SuperAdminPanel fetchData={fetchData} />
          )}
        </div>
      </div>
    </div>
  );
};

// Tip Management Page
export const AgencyTipsPage = () => {
  const { agencyUser, logout } = useAgencyAuth();
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: '', priority: '' });
  const [selectedTip, setSelectedTip] = useState(null);
  const navigate = useNavigate();

  const fetchTips = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filter.status) params.append('status', filter.status);
      if (filter.priority) params.append('priority', filter.priority);
      params.append('limit', '100');
      
      const res = await agencyApi.get(`/agency/tips?${params}`);
      setTips(res.data.tips || []);
    } catch (error) {
      if (error.response?.status === 401) {
        logout();
        navigate('/agency/login');
      }
    } finally {
      setLoading(false);
    }
  }, [filter, logout, navigate]);

  useEffect(() => {
    fetchTips();
  }, [fetchTips]);

  const updateTipStatus = async (tipId, status) => {
    try {
      await agencyApi.put(`/agency/tips/${tipId}/status?status=${status}`);
      toast.success(`Tip marked as ${status}`);
      fetchTips();
      setSelectedTip(null);
    } catch (error) {
      toast.error('Failed to update tip');
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 pt-20 px-4 pb-12">
      {/* Same navbar as dashboard */}
      <nav className="fixed top-0 w-full z-50 bg-slate-900/95 backdrop-blur-xl border-b border-cyan-500/30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/agency/dashboard')} className="text-slate-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <Shield className="w-8 h-8 text-cyan-400" />
            <span className="font-heading font-bold text-xl text-white">TIP MANAGEMENT</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-slate-400 text-sm">{agencyUser?.name}</span>
            <button onClick={logout} className="text-slate-400 hover:text-red-400 text-sm">Logout</button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto">
        {/* Filters */}
        <div className="mb-6 flex items-center gap-4 flex-wrap">
          <select
            value={filter.status}
            onChange={(e) => setFilter({ ...filter, status: e.target.value })}
            className="input-tactical px-3 py-2 text-sm"
          >
            <option value="">All Statuses</option>
            <option value="PENDING">Pending</option>
            <option value="UNDER_REVIEW">Under Review</option>
            <option value="VERIFIED">Verified</option>
            <option value="ACTIONABLE">Actionable</option>
            <option value="DISMISSED">Dismissed</option>
          </select>
          <select
            value={filter.priority}
            onChange={(e) => setFilter({ ...filter, priority: e.target.value })}
            className="input-tactical px-3 py-2 text-sm"
          >
            <option value="">All Priorities</option>
            <option value="CRITICAL">Critical</option>
            <option value="HIGH">High</option>
            <option value="MEDIUM">Medium</option>
            <option value="LOW">Low</option>
          </select>
          <button onClick={fetchTips} className="btn-secondary px-4 py-2 text-sm flex items-center gap-2">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>

        {/* Tips Table */}
        <div className="card-tactical overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-800">
              <tr>
                <th className="text-left text-xs text-slate-400 uppercase p-4">Fugitive</th>
                <th className="text-left text-xs text-slate-400 uppercase p-4">Tip Content</th>
                <th className="text-left text-xs text-slate-400 uppercase p-4">Location</th>
                <th className="text-left text-xs text-slate-400 uppercase p-4">Priority</th>
                <th className="text-left text-xs text-slate-400 uppercase p-4">Status</th>
                <th className="text-left text-xs text-slate-400 uppercase p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="p-8 text-center text-slate-500">Loading...</td></tr>
              ) : tips.length === 0 ? (
                <tr><td colSpan={6} className="p-8 text-center text-slate-500">No tips found</td></tr>
              ) : (
                tips.map((tip) => (
                  <tr key={tip.tip_id} className="border-t border-slate-800 hover:bg-slate-800/50">
                    <td className="p-4">
                      <p className="text-white font-semibold text-sm">{tip.fugitive_name}</p>
                      <p className="text-slate-500 text-xs">{tip.tipster_id}</p>
                    </td>
                    <td className="p-4">
                      <p className="text-slate-300 text-sm line-clamp-2">{tip.content}</p>
                      {tip.has_image && <span className="text-cyan-400 text-xs">📷 Has Image</span>}
                    </td>
                    <td className="p-4 text-slate-400 text-sm">{tip.location || 'N/A'}</td>
                    <td className="p-4">
                      <span className={`text-xs px-2 py-1 rounded ${
                        tip.priority === 'CRITICAL' ? 'bg-red-500/20 text-red-400' :
                        tip.priority === 'HIGH' ? 'bg-amber-500/20 text-amber-400' :
                        tip.priority === 'MEDIUM' ? 'bg-cyan-500/20 text-cyan-400' :
                        'bg-slate-700 text-slate-400'
                      }`}>
                        {tip.priority}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className={`text-xs px-2 py-1 rounded ${
                        tip.status === 'VERIFIED' ? 'bg-green-500/20 text-green-400' :
                        tip.status === 'ACTIONABLE' ? 'bg-purple-500/20 text-purple-400' :
                        tip.status === 'UNDER_REVIEW' ? 'bg-amber-500/20 text-amber-400' :
                        tip.status === 'DISMISSED' ? 'bg-slate-700 text-slate-500' :
                        'bg-cyan-500/20 text-cyan-400'
                      }`}>
                        {tip.status}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={() => setSelectedTip(tip)}
                          className="text-cyan-400 hover:text-cyan-300 text-sm"
                        >
                          Review
                        </button>
                        <button 
                          onClick={() => navigate(`/agency/analytics?fugitive=${tip.fugitive_id}`)}
                          className="text-purple-400 hover:text-purple-300 text-sm"
                        >
                          AI
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Tip Detail Modal */}
        {selectedTip && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="card-tactical p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-heading font-bold text-xl text-white">{selectedTip.fugitive_name}</h3>
                  <p className="text-slate-400 text-sm">{selectedTip.tipster_id} • {new Date(selectedTip.created_at).toLocaleString()}</p>
                </div>
                <button onClick={() => setSelectedTip(null)} className="text-slate-400 hover:text-white">✕</button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <p className="text-slate-400 text-xs uppercase mb-1">Tip Content</p>
                  <p className="text-white bg-slate-800 p-3 rounded">{selectedTip.content}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-slate-400 text-xs uppercase mb-1">Location</p>
                    <p className="text-white">{selectedTip.location || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-xs uppercase mb-1">Reward</p>
                    <p className="text-amber-400 font-bold">${selectedTip.reward_amount?.toLocaleString() || 0}</p>
                  </div>
                </div>

                {selectedTip.ai_analysis && (
                  <div>
                    <p className="text-slate-400 text-xs uppercase mb-1">AI Analysis</p>
                    <p className="text-slate-300 bg-slate-800 p-3 rounded text-sm">{selectedTip.ai_analysis}</p>
                  </div>
                )}

                <div className="flex gap-2 pt-4 border-t border-slate-800">
                  <button onClick={() => updateTipStatus(selectedTip.tip_id, 'UNDER_REVIEW')} className="btn-secondary px-3 py-2 text-sm">Mark Under Review</button>
                  <button onClick={() => updateTipStatus(selectedTip.tip_id, 'VERIFIED')} className="btn-primary px-3 py-2 text-sm bg-green-600">Verify</button>
                  <button onClick={() => updateTipStatus(selectedTip.tip_id, 'ACTIONABLE')} className="btn-primary px-3 py-2 text-sm bg-purple-600">Mark Actionable</button>
                  <button onClick={() => updateTipStatus(selectedTip.tip_id, 'DISMISSED')} className="btn-secondary px-3 py-2 text-sm text-red-400">Dismiss</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// AI Analytics Page
// ==================== AGENCY AI COMMAND CENTER ====================

export const AgencyAICommandCenter = () => {
  const { agencyUser, logout } = useAgencyAuth();
  const [activeTab, setActiveTab] = useState('assistant');
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFugitive, setSelectedFugitive] = useState(null);
  const [fugitives, setFugitives] = useState([]);
  const [threatAnalysis, setThreatAnalysis] = useState(null);
  const [patternData, setPatternData] = useState(null);
  const [crimeHotspots, setCrimeHotspots] = useState([]);
  const navigate = useNavigate();
  const messagesEndRef = React.useRef(null);

  useEffect(() => {
    fetchFugitives();
    fetchThreatAnalysis();
    fetchPatternData();
    // Add welcome message
    setMessages([{
      id: 'welcome',
      role: 'assistant',
      content: `Welcome to the Wysspr AI Command Center, ${agencyUser?.name || 'Agent'}. I'm your advanced law enforcement AI assistant, trained on criminal behavior patterns, fugitive tracking methodologies, and intelligence analysis.

**I can help you with:**
• Real-time threat assessment and fugitive profiling
• Predictive location analysis based on behavioral patterns
• Network analysis to identify associates and safe houses
• Crime pattern recognition and hotspot mapping
• Tactical recommendations for apprehension strategies
• Evidence correlation and case file analysis

What intelligence do you need today?`
    }]);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchFugitives = async () => {
    try {
      const res = await agencyApi.get('/fugitives?limit=100');
      setFugitives(res.data || []);
    } catch (error) {
      console.error('Failed to fetch fugitives');
    }
  };

  const fetchThreatAnalysis = async () => {
    try {
      const res = await agencyApi.get('/ai/threat-dashboard');
      setThreatAnalysis(res.data);
    } catch (error) {
      console.error('Failed to fetch threat analysis');
    }
  };

  const fetchPatternData = async () => {
    try {
      const res = await agencyApi.get('/agency/stats');
      setPatternData(res.data);
    } catch (error) {
      console.error('Failed to fetch pattern data');
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;
    
    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: inputMessage
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    
    try {
      const context = selectedFugitive 
        ? `[CONTEXT: Analyzing fugitive ${selectedFugitive.name}]\n` 
        : '';
      
      const res = await agencyApi.post('/agency/ai/chat', {
        message: context + inputMessage,
        context: {
          fugitive_id: selectedFugitive?.fugitive_id,
          agent_name: agencyUser?.name,
          agency: agencyUser?.agency_name
        }
      });
      
      setMessages(prev => [...prev, {
        id: Date.now().toString() + '_response',
        role: 'assistant',
        content: res.data.response
      }]);
    } catch (error) {
      setMessages(prev => [...prev, {
        id: Date.now().toString() + '_error',
        role: 'assistant',
        content: 'I apologize, but I encountered an error processing your request. Please try again or rephrase your query.',
        error: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const quickActions = [
    { icon: Target, label: 'High Priority Targets', action: () => setInputMessage('Show me the highest priority fugitives with recent tip activity') },
    { icon: MapPin, label: 'Predict Location', action: () => setInputMessage('Based on behavioral patterns, predict the most likely current location for the top 5 fugitives') },
    { icon: Network, label: 'Network Analysis', action: () => setInputMessage('Identify potential associate networks and known contacts for high-value targets') },
    { icon: AlertTriangle, label: 'Threat Assessment', action: () => setInputMessage('Generate a comprehensive threat assessment for fugitives in my region') },
    { icon: TrendingUp, label: 'Crime Patterns', action: () => setInputMessage('Analyze recent crime patterns and identify emerging hotspots') },
    { icon: Brain, label: 'Tactical Brief', action: () => setInputMessage('Provide tactical recommendations for apprehending armed and dangerous fugitives') }
  ];

  const tabs = [
    { id: 'assistant', label: 'AI Assistant', icon: Bot },
    { id: 'threats', label: 'Threat Matrix', icon: AlertTriangle },
    { id: 'predictions', label: 'Predictive Intel', icon: Brain },
    { id: 'patterns', label: 'Pattern Analysis', icon: Activity }
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Top Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-slate-900/95 backdrop-blur-xl border-b border-amber-500/30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/agency/dashboard')} className="text-slate-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="p-2 bg-amber-500/20 rounded-lg">
              <Brain className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <span className="font-heading font-bold text-xl text-white">WYSSPR AI COMMAND CENTER</span>
              <span className="hidden md:inline ml-2 text-xs text-amber-400 px-2 py-0.5 bg-amber-500/20 rounded">ADVANCED</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-slate-400 text-sm hidden md:block">{agencyUser?.name} • {agencyUser?.agency_name}</span>
            <button onClick={logout} className="text-red-400 hover:text-red-300 text-sm flex items-center gap-1">
              <LogOut className="w-4 h-4" />
              <span className="hidden md:inline">Logout</span>
            </button>
          </div>
        </div>
      </nav>

      <div className="pt-20 px-4 pb-8">
        <div className="max-w-7xl mx-auto">
          {/* Tab Navigation */}
          <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? 'bg-amber-500 text-slate-900'
                    : 'bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* AI Assistant Tab */}
          {activeTab === 'assistant' && (
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Main Chat Area */}
              <div className="lg:col-span-3 flex flex-col h-[calc(100vh-200px)]">
                {/* Context Selector */}
                <div className="bg-slate-900 border border-slate-700 rounded-t-lg p-4">
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="flex-1">
                      <label className="text-slate-400 text-xs mb-1 block">FOCUS CONTEXT</label>
                      <select
                        value={selectedFugitive?.fugitive_id || ''}
                        onChange={(e) => {
                          const f = fugitives.find(f => f.fugitive_id === e.target.value);
                          setSelectedFugitive(f || null);
                        }}
                        className="w-full bg-slate-800 border border-slate-600 text-white rounded px-3 py-2 text-sm"
                      >
                        <option value="">General Intelligence Query</option>
                        {fugitives.slice(0, 50).map(f => (
                          <option key={f.fugitive_id} value={f.fugitive_id}>
                            {f.name} - ${(f.reward_amount || 0).toLocaleString()}
                          </option>
                        ))}
                      </select>
                    </div>
                    {selectedFugitive && (
                      <div className="flex items-center gap-3 p-2 bg-amber-500/10 border border-amber-500/30 rounded">
                        <Target className="w-5 h-5 text-amber-400" />
                        <div>
                          <p className="text-white text-sm font-semibold">{selectedFugitive.name}</p>
                          <p className="text-amber-400 text-xs">${(selectedFugitive.reward_amount || 0).toLocaleString()} Reward</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Messages Area */}
                <div className="flex-1 bg-slate-900/50 border-x border-slate-700 overflow-y-auto p-4 space-y-4">
                  {messages.map(msg => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[80%] rounded-lg p-4 ${
                        msg.role === 'user'
                          ? 'bg-amber-500/20 border border-amber-500/30'
                          : msg.error
                          ? 'bg-red-500/10 border border-red-500/30'
                          : 'bg-slate-800 border border-slate-700'
                      }`}>
                        {msg.role === 'assistant' && (
                          <div className="flex items-center gap-2 mb-2">
                            <Brain className="w-4 h-4 text-amber-400" />
                            <span className="text-amber-400 text-xs font-semibold">WYSSPR AI</span>
                          </div>
                        )}
                        <p className={`text-sm whitespace-pre-wrap ${
                          msg.role === 'user' ? 'text-white' : 'text-slate-300'
                        }`}>
                          {msg.content}
                        </p>
                      </div>
                    </div>
                  ))}
                  {isLoading && (
                    <div className="flex justify-start">
                      <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
                        <div className="flex items-center gap-2">
                          <div className="animate-spin w-4 h-4 border-2 border-amber-400 border-t-transparent rounded-full" />
                          <span className="text-slate-400 text-sm">Analyzing intelligence...</span>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input Area */}
                <div className="bg-slate-900 border border-slate-700 rounded-b-lg p-4">
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                      placeholder="Ask for intelligence analysis, threat assessments, or tactical recommendations..."
                      className="flex-1 bg-slate-800 border border-slate-600 text-white rounded-lg px-4 py-3 text-sm placeholder-slate-500 focus:outline-none focus:border-amber-500"
                    />
                    <button
                      onClick={handleSendMessage}
                      disabled={isLoading || !inputMessage.trim()}
                      className="bg-amber-500 hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed text-slate-900 font-semibold px-6 py-3 rounded-lg flex items-center gap-2"
                    >
                      <Send className="w-4 h-4" />
                      <span className="hidden md:inline">Send</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Quick Actions Sidebar */}
              <div className="space-y-4">
                <div className="bg-slate-900 border border-slate-700 rounded-lg p-4">
                  <h3 className="font-bold text-white mb-3 flex items-center gap-2">
                    <Zap className="w-4 h-4 text-amber-400" />
                    Quick Intelligence
                  </h3>
                  <div className="space-y-2">
                    {quickActions.map((action, idx) => (
                      <button
                        key={idx}
                        onClick={action.action}
                        className="w-full text-left bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-amber-500/50 rounded p-3 text-sm text-slate-300 hover:text-white transition-all flex items-center gap-2"
                      >
                        <action.icon className="w-4 h-4 text-amber-400" />
                        {action.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Live Stats */}
                <div className="bg-slate-900 border border-slate-700 rounded-lg p-4">
                  <h3 className="font-bold text-white mb-3 flex items-center gap-2">
                    <Activity className="w-4 h-4 text-cyan-400" />
                    Live Intel
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-slate-400 text-sm">Critical Threats</span>
                      <span className="text-red-400 font-bold">{threatAnalysis?.summary?.critical_count || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400 text-sm">High Risk</span>
                      <span className="text-orange-400 font-bold">{threatAnalysis?.summary?.high_risk_count || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400 text-sm">Total Tracked</span>
                      <span className="text-cyan-400 font-bold">{threatAnalysis?.summary?.total_fugitives || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400 text-sm">Pending Tips</span>
                      <span className="text-amber-400 font-bold">{patternData?.tips?.pending || 0}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Threat Matrix Tab */}
          {activeTab === 'threats' && (
            <div className="space-y-6">
              {/* Threat Overview */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {[
                  { level: 'CRITICAL', count: threatAnalysis?.summary?.threat_breakdown?.CRITICAL || 0, color: 'red' },
                  { level: 'HIGH', count: threatAnalysis?.summary?.threat_breakdown?.HIGH || 0, color: 'orange' },
                  { level: 'ELEVATED', count: threatAnalysis?.summary?.threat_breakdown?.ELEVATED || 0, color: 'yellow' },
                  { level: 'MODERATE', count: threatAnalysis?.summary?.threat_breakdown?.MODERATE || 0, color: 'blue' },
                  { level: 'LOW', count: threatAnalysis?.summary?.threat_breakdown?.LOW || 0, color: 'green' }
                ].map(item => (
                  <div key={item.level} className={`bg-slate-900 border border-${item.color}-500/30 rounded-lg p-4`}>
                    <p className="text-slate-400 text-xs uppercase tracking-wider">{item.level}</p>
                    <p className={`text-3xl font-bold text-${item.color}-400`}>{item.count}</p>
                  </div>
                ))}
              </div>

              {/* Critical Threats */}
              <div className="bg-slate-900 border border-red-500/30 rounded-lg p-6">
                <h3 className="font-bold text-xl text-white mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-400 animate-pulse" />
                  CRITICAL THREATS - IMMEDIATE ACTION REQUIRED
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {threatAnalysis?.critical_threats?.map(threat => (
                    <div 
                      key={threat.fugitive_id}
                      className="bg-red-500/5 border border-red-500/30 rounded-lg p-4 cursor-pointer hover:bg-red-500/10 transition-all"
                      onClick={() => navigate(`/agency/analytics?fugitive=${threat.fugitive_id}`)}
                    >
                      <div className="flex gap-4">
                        {threat.image_url && (
                          <img src={threat.image_url} alt="" className="w-16 h-16 object-cover rounded" />
                        )}
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h4 className="font-bold text-white">{threat.name}</h4>
                            <span className="text-red-400 font-bold text-xl">{threat.score}/100</span>
                          </div>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {threat.top_factors?.slice(0, 3).map((f, i) => (
                              <span key={i} className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded">
                                {f.factor}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* High Risk Targets */}
              <div className="bg-slate-900 border border-orange-500/30 rounded-lg p-6">
                <h3 className="font-bold text-xl text-white mb-4 flex items-center gap-2">
                  <AlertOctagon className="w-5 h-5 text-orange-400" />
                  HIGH RISK FUGITIVES
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {threatAnalysis?.high_threats?.map(threat => (
                    <div 
                      key={threat.fugitive_id}
                      className="bg-slate-800 border border-slate-700 rounded-lg p-3 cursor-pointer hover:border-orange-500/50 transition-all"
                      onClick={() => navigate(`/agency/analytics?fugitive=${threat.fugitive_id}`)}
                    >
                      <div className="flex items-center gap-3">
                        {threat.image_url ? (
                          <img src={threat.image_url} alt="" className="w-12 h-12 object-cover rounded" />
                        ) : (
                          <div className="w-12 h-12 bg-slate-700 rounded flex items-center justify-center">
                            <User className="w-6 h-6 text-slate-500" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-semibold truncate">{threat.name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <div className="flex-1 h-2 bg-slate-700 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-gradient-to-r from-orange-500 to-red-500"
                                style={{ width: `${threat.score}%` }}
                              />
                            </div>
                            <span className="text-orange-400 text-xs font-bold">{threat.score}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Predictive Intel Tab */}
          {activeTab === 'predictions' && (
            <div className="text-center py-12">
              <Brain className="w-16 h-16 text-amber-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">Predictive Intelligence</h2>
              <p className="text-slate-400 mb-6">Select a fugitive to generate AI-powered location predictions and behavioral analysis</p>
              <button 
                onClick={() => navigate('/agency/analytics')}
                className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold px-6 py-3 rounded-lg"
              >
                Open Predictive Analysis Tool
              </button>
            </div>
          )}

          {/* Pattern Analysis Tab */}
          {activeTab === 'patterns' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-slate-900 border border-slate-700 rounded-lg p-6">
                <h3 className="font-bold text-xl text-white mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-cyan-400" />
                  Tip Activity Trends
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-slate-800 rounded">
                    <span className="text-slate-400">Tips Today</span>
                    <span className="text-cyan-400 font-bold text-xl">{patternData?.tips?.today || 0}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-800 rounded">
                    <span className="text-slate-400">Tips This Week</span>
                    <span className="text-cyan-400 font-bold text-xl">{patternData?.tips?.this_week || 0}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-800 rounded">
                    <span className="text-slate-400">High Priority (24h)</span>
                    <span className="text-red-400 font-bold text-xl">{patternData?.tips?.high_priority_24h || 0}</span>
                  </div>
                </div>
              </div>

              <div className="bg-slate-900 border border-slate-700 rounded-lg p-6">
                <h3 className="font-bold text-xl text-white mb-4 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-amber-400" />
                  Case Status Overview
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-slate-800 rounded">
                    <span className="text-slate-400">Active Cases</span>
                    <span className="text-amber-400 font-bold text-xl">{patternData?.cases?.active || 0}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-800 rounded">
                    <span className="text-slate-400">Pending Review</span>
                    <span className="text-yellow-400 font-bold text-xl">{patternData?.cases?.pending || 0}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-800 rounded">
                    <span className="text-slate-400">Resolved This Month</span>
                    <span className="text-green-400 font-bold text-xl">{patternData?.cases?.resolved_month || 0}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== EXISTING AGENCY ANALYTICS PAGE ====================

export const AgencyAnalyticsPage = () => {
  const { agencyUser, logout } = useAgencyAuth();
  const [fugitives, setFugitives] = useState([]);
  const [selectedFugitive, setSelectedFugitive] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [heatmap, setHeatmap] = useState(null);
  const [network, setNetwork] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Fetch fugitives with tips
    agencyApi.get('/fugitives?limit=100').then(res => {
      setFugitives(res.data || []);
    }).catch(() => {});

    // Check URL params
    const params = new URLSearchParams(window.location.search);
    const fugitiveId = params.get('fugitive');
    if (fugitiveId) {
      setSelectedFugitive(fugitiveId);
    }
  }, []);

  useEffect(() => {
    if (selectedFugitive) {
      runAnalysis();
    }
  }, [selectedFugitive]);

  const runAnalysis = async () => {
    if (!selectedFugitive) return;
    setLoading(true);
    try {
      const [analysisRes, heatmapRes, networkRes] = await Promise.all([
        agencyApi.get(`/agency/predictive/${selectedFugitive}`),
        agencyApi.get(`/agency/heatmap/${selectedFugitive}`),
        agencyApi.get(`/agency/network/${selectedFugitive}`)
      ]);
      setAnalysis(analysisRes.data);
      setHeatmap(heatmapRes.data);
      setNetwork(networkRes.data);
    } catch (error) {
      toast.error('Failed to generate analysis');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 pt-20 px-4 pb-12">
      <nav className="fixed top-0 w-full z-50 bg-slate-900/95 backdrop-blur-xl border-b border-cyan-500/30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/agency/dashboard')} className="text-slate-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <Brain className="w-8 h-8 text-purple-400" />
            <span className="font-heading font-bold text-xl text-white">PREDICTIVE INTELLIGENCE</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-slate-400 text-sm">{agencyUser?.name}</span>
            <button onClick={logout} className="text-slate-400 hover:text-red-400 text-sm">Logout</button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto">
        {/* Fugitive Selector */}
        <div className="mb-8 card-tactical p-6">
          <h3 className="font-heading font-bold text-lg text-white mb-4">Select Target for Analysis</h3>
          <div className="flex gap-4">
            <select
              value={selectedFugitive}
              onChange={(e) => setSelectedFugitive(e.target.value)}
              className="input-tactical px-4 py-3 flex-1"
            >
              <option value="">-- Select Fugitive --</option>
              {fugitives.map(f => (
                <option key={f.fugitive_id} value={f.fugitive_id}>
                  {f.name} - ${f.reward_amount?.toLocaleString() || 0} reward
                </option>
              ))}
            </select>
            <button 
              onClick={runAnalysis} 
              disabled={!selectedFugitive || loading}
              className="btn-primary px-6 py-3 flex items-center gap-2"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Brain className="w-5 h-5" />
                  Generate Analysis
                </>
              )}
            </button>
          </div>
        </div>

        {analysis && (
          <div className="space-y-6">
            {/* Prediction Summary */}
            <div className="grid md:grid-cols-4 gap-4">
              <div className="card-tactical p-4">
                <p className="text-slate-400 text-xs uppercase mb-1">Flight Risk</p>
                <p className={`text-3xl font-bold ${
                  analysis.flight_risk_score > 70 ? 'text-red-400' :
                  analysis.flight_risk_score > 40 ? 'text-amber-400' :
                  'text-green-400'
                }`}>{analysis.flight_risk_score}%</p>
              </div>
              <div className="card-tactical p-4">
                <p className="text-slate-400 text-xs uppercase mb-1">Movement Pattern</p>
                <p className="text-xl font-bold text-white capitalize">{analysis.movement_pattern?.replace('_', ' ')}</p>
              </div>
              <div className="card-tactical p-4">
                <p className="text-slate-400 text-xs uppercase mb-1">Confidence</p>
                <p className="text-3xl font-bold text-cyan-400">{analysis.confidence}%</p>
              </div>
              <div className="card-tactical p-4">
                <p className="text-slate-400 text-xs uppercase mb-1">Tips Analyzed</p>
                <p className="text-3xl font-bold text-white">{heatmap?.total_tips || 0}</p>
              </div>
            </div>

            {/* Next 48 Hours */}
            <div className="card-tactical p-6 border-amber-500/30">
              <h3 className="font-heading font-bold text-lg text-amber-400 mb-3 flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Next 48 Hours Prediction
              </h3>
              <p className="text-white">{analysis.next_48h_summary}</p>
            </div>

            {/* Predicted Regions */}
            {analysis.predicted_regions?.length > 0 && (
              <div className="card-tactical p-6">
                <h3 className="font-heading font-bold text-lg text-white mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-cyan-400" />
                  Predicted Locations
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  {analysis.predicted_regions.map((region, idx) => (
                    <div key={idx} className="bg-slate-800/50 p-4 rounded-sm">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-white font-semibold">{region.region}</p>
                        <span className={`text-sm font-bold ${
                          region.probability > 60 ? 'text-red-400' :
                          region.probability > 30 ? 'text-amber-400' :
                          'text-slate-400'
                        }`}>{region.probability}%</span>
                      </div>
                      <p className="text-slate-400 text-sm">{region.reasoning}</p>
                      <p className="text-slate-500 text-xs mt-2">{region.timeframe}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recommended Actions */}
            {analysis.recommended_actions?.length > 0 && (
              <div className="card-tactical p-6 border-green-500/30">
                <h3 className="font-heading font-bold text-lg text-green-400 mb-3 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  Recommended Actions
                </h3>
                <ul className="space-y-2">
                  {analysis.recommended_actions.map((action, idx) => (
                    <li key={idx} className="text-white flex items-start gap-2">
                      <ChevronRight className="w-4 h-4 text-green-400 mt-1 flex-shrink-0" />
                      {action}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Network Analysis */}
            {network && (
              <div className="card-tactical p-6">
                <h3 className="font-heading font-bold text-lg text-white mb-4 flex items-center gap-2">
                  <Network className="w-5 h-5 text-amber-400" />
                  Network Analysis
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  {network.associates?.length > 0 && (
                    <div>
                      <p className="text-slate-400 text-xs uppercase mb-2">Known Associates</p>
                      <div className="space-y-2">
                        {network.associates.map((assoc, idx) => (
                          <div key={idx} className="bg-slate-800/50 p-3 rounded-sm">
                            <p className="text-white font-semibold">{assoc.name}</p>
                            <p className="text-slate-400 text-sm">{assoc.relationship}</p>
                            {assoc.notes && <p className="text-slate-500 text-xs">{assoc.notes}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {network.vehicles?.length > 0 && (
                    <div>
                      <p className="text-slate-400 text-xs uppercase mb-2">Vehicles</p>
                      <div className="space-y-2">
                        {network.vehicles.map((v, idx) => (
                          <div key={idx} className="bg-slate-800/50 p-3 rounded-sm">
                            <p className="text-white">{v.description}</p>
                            {v.plate && <p className="text-cyan-400 text-sm">Plate: {v.plate}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {network.locations?.length > 0 && (
                    <div>
                      <p className="text-slate-400 text-xs uppercase mb-2">Known Locations</p>
                      <div className="space-y-2">
                        {network.locations.map((loc, idx) => (
                          <div key={idx} className="bg-slate-800/50 p-3 rounded-sm">
                            <p className="text-white">{loc.name}</p>
                            <p className="text-slate-400 text-sm capitalize">{loc.type}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Heat Map Zones */}
            {heatmap?.zones?.length > 0 && (
              <div className="card-tactical p-6">
                <h3 className="font-heading font-bold text-lg text-white mb-4 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-red-400" />
                  Activity Heat Zones
                </h3>
                <div className="space-y-2">
                  {heatmap.zones.slice(0, 10).map((zone, idx) => (
                    <div key={idx} className="flex items-center gap-4 bg-slate-800/50 p-3 rounded-sm">
                      <div className="flex-1">
                        <p className="text-white font-semibold">{zone.location}</p>
                        <p className="text-slate-500 text-xs">{zone.tip_count} tips • {zone.high_priority_count} high priority</p>
                      </div>
                      <div className="w-32">
                        <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${
                              zone.probability > 70 ? 'bg-red-500' :
                              zone.probability > 40 ? 'bg-amber-500' :
                              'bg-cyan-500'
                            }`}
                            style={{ width: `${zone.probability}%` }}
                          />
                        </div>
                      </div>
                      <span className="text-white font-bold w-12 text-right">{zone.probability}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Agency Profile Page
export const AgencyProfilePage = () => {
  const { agencyUser, logout, loading: authLoading } = useAgencyAuth();
  const navigate = useNavigate();

  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-cyan-500 animate-spin" />
      </div>
    );
  }

  if (!agencyUser) {
    return <Navigate to="/agency/login" />;
  }

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Navbar */}
      <nav className="fixed top-0 w-full z-50 bg-slate-900/95 backdrop-blur-xl border-b border-cyan-500/30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/agency/dashboard')} className="text-slate-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <Shield className="w-8 h-8 text-cyan-400" />
            <span className="font-heading font-bold text-xl text-white">MY PROFILE</span>
          </div>
          <button onClick={logout} className="text-slate-400 hover:text-red-400">
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </nav>

      <div className="pt-24 px-4 pb-12">
        <div className="max-w-2xl mx-auto">
          {/* Profile Header */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-tactical p-8 mb-6"
          >
            <div className="flex items-center gap-6">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center">
                <User className="w-12 h-12 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">{agencyUser.name}</h1>
                <p className="text-cyan-400">{agencyUser.role?.replace('_', ' ').toUpperCase()}</p>
                <p className="text-slate-400 text-sm">{agencyUser.agency_name}</p>
              </div>
            </div>
          </motion.div>

          {/* Credentials Card */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="card-tactical p-6 mb-6 border-cyan-500/30"
          >
            <h2 className="font-heading font-bold text-lg text-white uppercase flex items-center gap-2 mb-6">
              <Badge className="w-5 h-5 text-cyan-400" />
              Law Enforcement Credentials
            </h2>
            
            <div className="space-y-4">
              <div className="bg-slate-800/50 p-4 rounded-sm flex items-center gap-4">
                <div className="w-12 h-12 bg-cyan-500/20 rounded-full flex items-center justify-center">
                  <Hash className="w-6 h-6 text-cyan-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-xs uppercase tracking-wider">Agent ID</p>
                  <p className="text-white text-xl font-mono font-bold">{agencyUser.user_id}</p>
                </div>
              </div>

              <div className="bg-slate-800/50 p-4 rounded-sm flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center">
                  <Badge className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-xs uppercase tracking-wider">Badge Number</p>
                  <p className="text-white text-xl font-mono font-bold">{agencyUser.badge_number || 'Not Assigned'}</p>
                </div>
              </div>

              <div className="bg-slate-800/50 p-4 rounded-sm flex items-center gap-4">
                <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                  <Building className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-xs uppercase tracking-wider">Agency</p>
                  <p className="text-white text-lg font-semibold">{agencyUser.agency_name}</p>
                  <p className="text-slate-500 text-sm">Agency ID: {agencyUser.agency_id}</p>
                </div>
              </div>

              <div className="bg-slate-800/50 p-4 rounded-sm flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-500/20 rounded-full flex items-center justify-center">
                  <Mail className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-xs uppercase tracking-wider">Email</p>
                  <p className="text-white text-lg">{agencyUser.email}</p>
                </div>
              </div>

              <div className="bg-slate-800/50 p-4 rounded-sm flex items-center gap-4">
                <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                  <Shield className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-xs uppercase tracking-wider">Access Level</p>
                  <p className="text-white text-lg font-semibold capitalize">{agencyUser.role?.replace('_', ' ')}</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Security Notice */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-slate-800/30 p-4 rounded-sm border border-slate-700"
          >
            <p className="text-slate-500 text-xs text-center">
              🔒 Your credentials are securely stored and encrypted. Contact your agency administrator for any changes.
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

// Agency Fugitives Database Page
export const AgencyFugitivesPage = () => {
  const { agencyUser, logout, loading: authLoading } = useAgencyAuth();
  const [fugitives, setFugitives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSource, setSelectedSource] = useState('ALL');
  const [sourceCounts, setSourceCounts] = useState({});
  const [captureModal, setCaptureModal] = useState(null);
  const [captureLoading, setCaptureLoading] = useState(false);
  const navigate = useNavigate();

  const fetchFugitives = useCallback(async () => {
    if (!agencyUser) return;
    try {
      const params = selectedSource !== 'ALL' ? `?source=${selectedSource}` : '';
      const res = await agencyApi.get(`/agency/fugitives/by-source${params}`);
      setFugitives(res.data.fugitives || []);
      setSourceCounts(res.data.by_source || {});
    } catch (error) {
      if (error.response?.status === 401) {
        logout();
        navigate('/agency/login');
      }
      toast.error('Failed to load fugitives');
    } finally {
      setLoading(false);
    }
  }, [selectedSource, logout, navigate, agencyUser]);

  useEffect(() => {
    if (agencyUser) {
      fetchFugitives();
    }
  }, [fetchFugitives, agencyUser]);

  const handleMarkCaptured = async (fugitive) => {
    setCaptureLoading(true);
    try {
      await agencyApi.post('/agency/fugitive/capture', {
        fugitive_id: fugitive.fugitive_id,
        capture_date: new Date().toISOString(),
        capture_location: captureModal?.location || 'Not specified',
        notes: captureModal?.notes || ''
      });
      toast.success(`${fugitive.name} marked as CAPTURED!`);
      setCaptureModal(null);
      fetchFugitives();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as captured');
    } finally {
      setCaptureLoading(false);
    }
  };

  const sources = [
    { id: 'ALL', name: 'All Agencies', icon: '🎯', color: 'cyan' },
    { id: 'FBI', name: 'FBI', icon: '🔵', color: 'blue' },
    { id: 'US_MARSHALS', name: 'US Marshals', icon: '⚔️', color: 'purple' },
    { id: 'DEA', name: 'DEA', icon: '💊', color: 'green' },
    { id: 'ATF', name: 'ATF', icon: '🔫', color: 'orange' },
    { id: 'ICE', name: 'ICE', icon: '🛂', color: 'teal' }
  ];

  const formatReward = (amount) => {
    if (!amount) return 'Unknown';
    if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
    if (amount >= 1000) return `$${(amount / 1000).toFixed(0)}K`;
    return `$${amount}`;
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-cyan-500 animate-spin" />
      </div>
    );
  }

  if (!agencyUser) {
    return <Navigate to="/agency/login" />;
  }

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Navbar */}
      <nav className="fixed top-0 w-full z-50 bg-slate-900/95 backdrop-blur-xl border-b border-cyan-500/30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/agency/dashboard')} className="text-slate-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <Shield className="w-8 h-8 text-cyan-400" />
            <span className="font-heading font-bold text-xl text-white">FUGITIVES DATABASE</span>
          </div>
          <div className="flex items-center gap-4">
            <button 
              onClick={async () => {
                setLoading(true);
                try {
                  await agencyApi.post('/agency/sync/all-live');
                  toast.success('Databases synced successfully');
                  fetchFugitives();
                } catch (error) {
                  toast.error('Failed to sync databases');
                }
                setLoading(false);
              }}
              className="btn-primary px-4 py-2 text-sm flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Sync All
            </button>
            <button onClick={logout} className="text-slate-400 hover:text-red-400">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </nav>

      <div className="pt-20 px-4 pb-12">
        <div className="max-w-7xl mx-auto">
          {/* Source Filters */}
          <div className="mb-6 flex flex-wrap gap-2">
            {sources.map((source) => (
              <button
                key={source.id}
                onClick={() => setSelectedSource(source.id)}
                className={`px-4 py-2 rounded-sm text-sm font-semibold flex items-center gap-2 transition-colors ${
                  selectedSource === source.id
                    ? 'bg-cyan-500 text-white'
                    : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                }`}
              >
                <span>{source.icon}</span>
                {source.name}
                {source.id !== 'ALL' && sourceCounts[source.id] && (
                  <span className="bg-slate-900/50 px-2 py-0.5 rounded text-xs">
                    {sourceCounts[source.id]}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            {sources.slice(1).map((source) => (
              <div key={source.id} className="card-tactical p-4 text-center">
                <span className="text-2xl">{source.icon}</span>
                <p className="text-2xl font-bold text-white mt-2">{sourceCounts[source.id] || 0}</p>
                <p className="text-slate-500 text-xs">{source.name}</p>
              </div>
            ))}
          </div>

          {/* Fugitives Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="w-8 h-8 text-cyan-500 animate-spin" />
            </div>
          ) : fugitives.length === 0 ? (
            <div className="text-center py-20">
              <Target className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400 text-lg">No fugitives found</p>
              <p className="text-slate-500 text-sm">Click "Sync All" to fetch latest data from federal databases</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {fugitives.map((fugitive) => (
                <motion.div
                  key={fugitive.fugitive_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="card-tactical p-4 hover:border-cyan-500/50 transition-colors cursor-pointer"
                  onClick={() => navigate(`/agency/analytics?fugitive=${fugitive.fugitive_id}`)}
                >
                  <div className="flex gap-4">
                    {fugitive.image ? (
                      <img 
                        src={fugitive.image} 
                        alt={fugitive.name}
                        className="w-20 h-24 object-cover rounded-sm bg-slate-800"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-20 h-24 bg-slate-800 rounded-sm flex items-center justify-center">
                        <User className="w-8 h-8 text-slate-600" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="text-white font-bold text-sm truncate">{fugitive.name}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-sm whitespace-nowrap ${
                          fugitive.source_agency === 'FBI' ? 'bg-blue-500/20 text-blue-400' :
                          fugitive.source_agency === 'US_MARSHALS' ? 'bg-purple-500/20 text-purple-400' :
                          fugitive.source_agency === 'DEA' ? 'bg-green-500/20 text-green-400' :
                          fugitive.source_agency === 'ATF' ? 'bg-orange-500/20 text-orange-400' :
                          'bg-cyan-500/20 text-cyan-400'
                        }`}>
                          {fugitive.source_agency?.replace('_', ' ')}
                        </span>
                      </div>
                      <p className="text-amber-400 font-bold text-lg mt-1">
                        {formatReward(fugitive.reward_amount)}
                      </p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {fugitive.charges?.slice(0, 2).map((charge, idx) => (
                          <span key={idx} className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded-sm">
                            {charge.length > 20 ? charge.substring(0, 20) + '...' : charge}
                          </span>
                        ))}
                      </div>
                      {fugitive.last_known_location && (
                        <p className="text-slate-500 text-xs mt-2 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {fugitive.last_known_location}
                        </p>
                      )}
                      {/* Capture Button */}
                      {fugitive.status?.toLowerCase() !== 'captured' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setCaptureModal({ fugitive, location: '', notes: '' });
                          }}
                          className="mt-3 w-full py-2 bg-green-500/20 border border-green-500/50 text-green-400 rounded text-xs font-semibold hover:bg-green-500/30 transition-all flex items-center justify-center gap-2"
                        >
                          <CheckCircle className="w-4 h-4" />
                          Mark as Captured
                        </button>
                      )}
                      {fugitive.status?.toLowerCase() === 'captured' && (
                        <div className="mt-3 w-full py-2 bg-green-500/30 text-green-400 rounded text-xs font-bold text-center">
                          ✓ CAPTURED
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {/* Capture Confirmation Modal */}
          {captureModal && (
            <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="bg-slate-900 border border-green-500/50 rounded-lg max-w-md w-full p-6"
              >
                <h3 className="font-bold text-xl text-white mb-2 flex items-center gap-2">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                  Confirm Capture
                </h3>
                <p className="text-slate-400 mb-4">
                  Mark <span className="text-white font-semibold">{captureModal.fugitive.name}</span> as captured?
                </p>
                
                <div className="space-y-4 mb-6">
                  <div>
                    <label className="text-slate-400 text-sm block mb-1">Capture Location</label>
                    <input
                      type="text"
                      value={captureModal.location}
                      onChange={(e) => setCaptureModal(prev => ({ ...prev, location: e.target.value }))}
                      placeholder="City, State"
                      className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-slate-400 text-sm block mb-1">Notes (Optional)</label>
                    <textarea
                      value={captureModal.notes}
                      onChange={(e) => setCaptureModal(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Any additional details..."
                      rows={2}
                      className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white text-sm resize-none"
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setCaptureModal(null)}
                    className="flex-1 py-2 bg-slate-700 text-white rounded font-semibold hover:bg-slate-600"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleMarkCaptured(captureModal.fugitive)}
                    disabled={captureLoading}
                    className="flex-1 py-2 bg-green-500 text-white rounded font-semibold hover:bg-green-400 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {captureLoading ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        Confirm Capture
                      </>
                    )}
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default {
  AgencyAuthProvider,
  AgencyLoginPage,
  AgencyDashboard,
  AgencyTipsPage,
  AgencyAnalyticsPage,
  AgencyProfilePage,
  AgencyFugitivesPage,
  AgencyAICommandCenter
};
